"use client";

import { type ILocation } from '@order-up/models';
import { X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { generateSmartDateOptions, generateTimeSlots } from '../schedulingUtils';

// Add scrollbar hiding styles
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * ScheduleModal Component Props
 */
interface ScheduleModalProps {
    /** Whether the modal is currently open */
    isOpen: boolean;
    /** Callback to close the modal */
    onClose: () => void;
    /** Order type (pickup or delivery) */
    orderType: 'pickup' | 'delivery';
    /** Callback when schedule is confirmed (date is undefined for ASAP) */
    onScheduleConfirm: (date: Date | undefined, time: string) => void;
    /** Currently selected date (optional) */
    selectedDate?: Date;
    /** Currently selected time (optional) */
    selectedTime?: string;
    /** Restaurant data with operating hours and ordering config */
    restaurant?: ILocation | null;
    /** Whether restaurant is currently open */
    isRestaurantOpen?: boolean;
}

// DateOption interface is imported from schedulingUtils

// Time slot generation is now handled by generateTimeSlots from schedulingUtils

/**
 * Check if a date is today
 */
const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
};

/**
 * ScheduleModal Component
 *
 * Modal for selecting date and time for scheduled orders.
 * Provides a clean interface similar to industry-standard ordering apps.
 * Only shows valid time slots based on restaurant operating hours.
 *
 * @component
 */
export default function ScheduleModal({
    isOpen,
    onClose,
    orderType: _orderType, // Not used currently (pickup-only)
    onScheduleConfirm,
    selectedDate,
    selectedTime: initialSelectedTime,
    restaurant: locationRecord,
    isRestaurantOpen: _isLocationOpen = false // Not used - restaurant hours checked via schedulingUtils
}: ScheduleModalProps) {
    // State Management
    const [currentDate, setCurrentDate] = useState<Date>(selectedDate || new Date());
    const [selectedTime, setSelectedTime] = useState<string>(initialSelectedTime || 'ASAP');
    const [showMoreDates, setShowMoreDates] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldShow, setShouldShow] = useState(false);

    // Refs for click outside detection
    const desktopModalRef = useRef<HTMLDivElement>(null);
    const mobileModalRef = useRef<HTMLDivElement>(null);

    // Generate smart date options (only showing available dates)
    const dateOptions = useMemo(() => generateSmartDateOptions(locationRecord, 7), [locationRecord]);

    // Generate time options based on selected date and restaurant hours
    const timeOptions = useMemo(() => {
        const selectedIsToday = isToday(currentDate);
        // Only include ASAP if it's today
        const showASAP = selectedIsToday;

        return generateTimeSlots(
            locationRecord,
            currentDate,
            showASAP,
            locationRecord?.settings
        );
    }, [currentDate, locationRecord]);

    // Auto-select first available date if current date is not available
    useEffect(() => {
        if (!isOpen) return;

        const currentDateIsAvailable = dateOptions.some(
            option => option.isAvailable &&
            option.date.toDateString() === currentDate.toDateString()
        );

        if (!currentDateIsAvailable && dateOptions.length > 0) {
            const firstAvailableDate = dateOptions.find(option => option.isAvailable);
            if (firstAvailableDate) {
                setCurrentDate(firstAvailableDate.date);
            }
        }
    }, [isOpen, dateOptions, currentDate]);

    // Auto-select first available time if current selection is invalid
    useEffect(() => {
        if (!isOpen || timeOptions.length === 0) return;

        // Check if current selectedTime is valid in timeOptions
        const isValidSelection = timeOptions.includes(selectedTime);

        if (!isValidSelection) {
            // Auto-select first available time
            setSelectedTime(timeOptions[0]);
        }
    }, [isOpen, timeOptions, selectedTime]);

    // Display date options (first 2 or all based on showMoreDates)
    const visibleDateOptions = showMoreDates ? dateOptions : dateOptions.slice(0, 2);

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const desktopModal = desktopModalRef.current;
            const mobileModal = mobileModalRef.current;
            const isLargeScreen = window.innerWidth >= 1024;
            const activeModal = isLargeScreen ? desktopModal : mobileModal;

            if (activeModal && !activeModal.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Handle animation state
    useEffect(() => {
        if (isOpen) {
            setIsAnimating(true);
            const timer = setTimeout(() => {
                setShouldShow(true);
            }, 10);
            return () => clearTimeout(timer);
        } else {
            setShouldShow(false);
        }
    }, [isOpen]);

    // Handle date selection
    const handleDateSelect = (date: Date) => {
        setCurrentDate(date);
        // Auto-collapse dates after selection if expanded
        if (showMoreDates) {
            setShowMoreDates(false);
        }
    };

    // Handle time selection
    const handleTimeSelect = (time: string) => {
        setSelectedTime(time);
    };

    // Handle confirm
    const handleConfirm = () => {
        // Validate that selected time is actually available
        if (!timeOptions.includes(selectedTime)) {
            console.error('Invalid time selection:', selectedTime);
            return;
        }

        if (selectedTime === 'ASAP') {
            // ASAP: Pass undefined so exact time is calculated at checkout
            onScheduleConfirm(undefined, 'ASAP');
        } else {
            // Combine selected date with selected time
            const [timeStr, period] = selectedTime.split(' ');
            const [hours, minutes] = timeStr.split(':').map(Number);

            const scheduledDateTime = new Date(currentDate);
            let hour = hours;
            if (period === 'PM' && hours !== 12) hour += 12;
            if (period === 'AM' && hours === 12) hour = 0;

            scheduledDateTime.setHours(hour, minutes || 0, 0, 0);

            onScheduleConfirm(scheduledDateTime, selectedTime);
        }
        onClose();
    };

    if (!isOpen && !isAnimating) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: scrollbarHideStyles }} />
            <div
                className={`fixed inset-0 z-50 transition-all duration-300 ease-in-out ${shouldShow ? 'opacity-100' : 'opacity-0'}`}
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onTransitionEnd={() => {
                    if (!isOpen) setIsAnimating(false);
                }}
            >
                {/* Desktop Modal Container */}
                <div className="hidden lg:flex items-center justify-center p-4 h-full">
                    <div
                        ref={desktopModalRef}
                        className={`bg-white rounded-2xl max-w-md w-full max-h-[90dvh] shadow-2xl transition-all duration-300 ease-in-out transform flex flex-col ${shouldShow ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                            }`}
                    >
                        {/* Fixed Header */}
                        <div className="flex items-center justify-between p-6 pb-4 shrink-0">
                            <h2 className="text-xl font-bold text-gray-900">Order time</h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors pointer-coarse:p-3"
                                aria-label="Close modal"
                            >
                                <X className="h-5 w-5 text-gray-500" aria-hidden />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto px-6">
                            {/* Date Selection - Desktop Grid */}
                            <div className="mb-6 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    {visibleDateOptions.map((option, index) => {
                                        // Compare the full date — getDate() alone (day-of-month)
                                        // wrongly matched e.g. Jul 5 and Aug 5.
                                        const isSelected =
                                            option.date.toDateString() === currentDate.toDateString();
                                        return (
                                            <button
                                                key={index}
                                                onClick={() => option.isAvailable && handleDateSelect(option.date)}
                                                disabled={!option.isAvailable}
                                                className={`px-4 py-3.5 rounded-2xl text-sm font-semibold transition-all ${
                                                    !option.isAvailable
                                                        ? 'bg-gray-50 border border-gray-100 text-gray-400 cursor-not-allowed'
                                                        : isSelected
                                                        ? 'bg-black text-white'
                                                        : 'bg-white border border-gray-200 text-gray-900 hover:border-gray-300'
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span>{option.label}</span>
                                                    <span className={!option.isAvailable ? 'text-gray-400' : isSelected ? 'text-white' : 'text-gray-600'}>{option.shortLabel}</span>
                                                </div>
                                                {!option.isAvailable && (
                                                    <div className="text-xs text-gray-400 mt-1">Closed</div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* More/Less Dates Button */}
                                <button
                                    onClick={() => setShowMoreDates(!showMoreDates)}
                                    className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-2xl text-sm font-semibold text-gray-900 hover:border-gray-300 transition-all flex items-center justify-center gap-2"
                                >
                                    <span>{showMoreDates ? 'Less dates' : 'More dates'}</span>
                                    <span className={`transform transition-transform text-xs ${showMoreDates ? 'rotate-180' : ''}`}>▼</span>
                                </button>
                            </div>

                            {/* Time Selection - Radio Buttons */}
                            <div className="space-y-0 mb-6 max-h-80 overflow-y-auto">
                                {timeOptions.map((time) => {
                                    const isSelected = selectedTime === time;
                                    return (
                                        <button
                                            key={time}
                                            onClick={() => handleTimeSelect(time)}
                                            className="w-full px-5 py-4 text-left transition-colors flex items-center gap-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected
                                                ? 'border-black'
                                                : 'border-gray-300'
                                                }`}>
                                                {isSelected && (
                                                    <div className="w-3 h-3 rounded-full bg-black" />
                                                )}
                                            </div>
                                            <span className={`font-normal text-base ${isSelected ? 'text-black font-medium' : 'text-gray-900'}`}>
                                                {time}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Fixed Bottom Button */}
                        <div className="p-6 pt-4 shrink-0 border-t border-gray-100">
                            <button
                                onClick={handleConfirm}
                                className="w-full bg-[var(--menu-primary,#dc2626)] text-white py-4 rounded-2xl font-semibold text-base hover:bg-[var(--menu-primary-hover,#b92020)] transition-colors shadow-sm"
                            >
                                {selectedTime === 'ASAP' ? 'Order ASAP' : `Order ${selectedTime}`}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile/Tablet Bottom Sheet Container */}
                <div className="lg:hidden flex flex-col h-full">
                    <div className="flex-1" onClick={onClose} />

                    <div
                        ref={mobileModalRef}
                        className={`bg-white rounded-t-3xl shadow-2xl flex flex-col pb-[env(safe-area-inset-bottom,0px)] transition-all duration-300 ease-in-out transform ${shouldShow ? 'translate-y-0' : 'translate-y-full'
                            }`}
                        style={{ maxHeight: '85dvh' }}
                    >
                        {/* Handle Bar */}
                        <div className="flex justify-center py-3 shrink-0">
                            <div className="w-10 h-1 bg-gray-300 rounded-full" />
                        </div>

                        {/* Fixed Mobile Header */}
                        <div className="flex items-center justify-between px-6 pb-4 shrink-0">
                            <h2 className="text-xl font-bold text-gray-900">Order time</h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors pointer-coarse:p-3"
                                aria-label="Close modal"
                            >
                                <X className="h-5 w-5 text-gray-500" aria-hidden />
                            </button>
                        </div>

                        {/* Scrollable Mobile Content */}
                        <div className="flex-1 overflow-y-auto py-4">
                            {/* Date Selection - Horizontal Scroll */}
                            <div className="mb-6">
                                {/* Horizontal Scrollable Date Cards - Show all dates on mobile */}
                                <div className="flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-hide snap-x snap-mandatory">
                                    {dateOptions.map((option, index) => {
                                        // Compare the full date — getDate() alone (day-of-month)
                                        // wrongly matched e.g. Jul 5 and Aug 5.
                                        const isSelected =
                                            option.date.toDateString() === currentDate.toDateString();
                                        return (
                                            <button
                                                key={index}
                                                onClick={() => option.isAvailable && handleDateSelect(option.date)}
                                                disabled={!option.isAvailable}
                                                className={`shrink-0 w-32 px-4 py-3.5 rounded-2xl text-sm font-semibold transition-all snap-start ${
                                                    !option.isAvailable
                                                        ? 'bg-gray-50 border border-gray-100 text-gray-400 cursor-not-allowed'
                                                        : isSelected
                                                        ? 'bg-black text-white'
                                                        : 'bg-white border border-gray-200 text-gray-900 active:scale-95'
                                                    }`}
                                            >
                                                <div className="flex flex-col items-center gap-1">
                                                    <span>{option.label}</span>
                                                    <span className={`text-xs ${!option.isAvailable ? 'text-gray-400' : isSelected ? 'text-white/80' : 'text-gray-600'}`}>{option.shortLabel}</span>
                                                    {!option.isAvailable && (
                                                        <span className="text-xs text-gray-400">Closed</span>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Time Selection - Radio Buttons */}
                            <div className="space-y-0 mb-6 px-6">
                                {timeOptions.map((time) => {
                                    const isSelected = selectedTime === time;
                                    return (
                                        <button
                                            key={time}
                                            onClick={() => handleTimeSelect(time)}
                                            className="w-full px-5 py-4 text-left transition-colors flex items-center gap-3 active:bg-gray-50 border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected
                                                ? 'border-black'
                                                : 'border-gray-300'
                                                }`}>
                                                {isSelected && (
                                                    <div className="w-3 h-3 rounded-full bg-black" />
                                                )}
                                            </div>
                                            <span className={`font-normal text-base ${isSelected ? 'text-black font-medium' : 'text-gray-900'}`}>
                                                {time}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Fixed Bottom Button */}
                        <div className="px-6 pb-6 pt-4 border-t border-gray-100 shrink-0">
                            <button
                                onClick={handleConfirm}
                                className="w-full bg-[var(--menu-primary,#dc2626)] text-white py-4 rounded-2xl font-semibold text-base hover:bg-[var(--menu-primary-hover,#b92020)] transition-colors shadow-sm"
                            >
                                {selectedTime === 'ASAP' ? 'Order ASAP' : `Order ${selectedTime}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

